import { v2Api } from './v2Client';

const LOG = '[SquashAPI memberships]';

/**
 * Abonnements du club (formules = abonnements à vendre, abonnés, encaissement, validation) — voir Controllers/MembershipAdminController.cs
 * et Services/Membership côté API. Lecture : staff ; actions d'argent : administrateur uniquement (403 sinon).
 */

export const MEMBERSHIP_STATUS_LABELS = {
  pending_payment: 'À payer',
  pending_approval: 'À valider',
  active: 'Actif',
  rejected: 'Refusé',
  cancelled: 'Annulé',
  expired: 'Expiré',
};

export const MEMBERSHIP_PAYMENT_STATUS_LABELS = {
  unpaid: 'Non payée',
  paid: 'Payée',
  partially_refunded: 'Part. remboursée',
  refunded: 'Remboursée',
};

export const MEMBERSHIP_METHOD_LABELS = {
  stripe: 'En ligne',
  on_site: 'Au club',
  free: 'Gratuit',
};

export const ON_SITE_METHODS = [
  { value: 'cash', label: 'Espèces' },
  { value: 'terminal', label: 'Terminal (carte)' },
  { value: 'transfer', label: 'Virement' },
];

export const MEMBERSHIP_EVENT_LABELS = {
  created: 'Demande créée',
  payment_started: 'Paiement en ligne démarré',
  paid_online: 'Payé en ligne',
  marked_paid_on_site: 'Réglé au club',
  approved: 'Validé',
  rejected: 'Refusé',
  cancelled: 'Annulé',
  payment_abandoned: 'Paiement abandonné',
  refund_decided: 'Remboursement décidé',
  refund_declined: 'Sans remboursement',
  refund_on_site_to_do: 'À rembourser au club',
  late_payment_refunded: 'Paiement tardif remboursé',
  payment_mismatch: 'Incohérence de paiement',
  expired: 'Expiré',
  reminder_sent: 'Rappel envoyé',
  note_updated: 'Modification',
  wallet_credited: 'Crédit wallet versé',
  wallet_credit_reversed: 'Crédit wallet repris',
};

/** Erreur API → message affichable (les erreurs métier portent { code, message }). */
export function membershipErrorMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

function normalizePlan(p) {
  if (!p) return null;
  return {
    ...p,
    id: String(p.id),
    price_cents: Number(p.price_cents ?? 0) || 0,
    wallet_credit_cents: Number(p.wallet_credit_cents ?? 0) || 0,
    court_discount_percent: Number(p.court_discount_percent ?? 0) || 0,
    max_members: p.max_members ?? null,
    remaining: p.remaining ?? null,
    subscription_count: Number(p.subscription_count ?? 0) || 0,
    active_count: Number(p.active_count ?? 0) || 0,
    pending_payment_count: Number(p.pending_payment_count ?? 0) || 0,
    pending_approval_count: Number(p.pending_approval_count ?? 0) || 0,
    pricing_locked: Boolean(p.pricing_locked),
    active: Boolean(p.active),
  };
}

function normalizeSubscription(s) {
  if (!s) return null;
  return {
    ...s,
    id: String(s.id),
    player_id: s.player_id != null ? String(s.player_id) : null,
    plan_id: s.plan_id != null ? String(s.plan_id) : null,
    price_cents: Number(s.price_cents ?? 0) || 0,
    wallet_credit_cents: Number(s.wallet_credit_cents ?? 0) || 0,
    refunded_cents: Number(s.refunded_cents ?? 0) || 0,
    refundable_online_cents: Number(s.refundable_online_cents ?? 0) || 0,
    is_effective: Boolean(s.is_effective),
  };
}

async function post(path, payload, what) {
  try {
    const { data } = await v2Api.post(path, payload);
    return data;
  } catch (err) {
    console.error(LOG, `${what} échoué`, { payload, err });
    throw err;
  }
}

// --- Formules -----------------------------------------------------------------------------------------

export async function listMembershipPlans() {
  const { data } = await v2Api.get('/MembershipAdmin/plans');
  return (Array.isArray(data) ? data : []).map(normalizePlan).filter(Boolean);
}

export async function upsertMembershipPlan(payload) {
  return normalizePlan(await post('/MembershipAdmin/plan_upsert', payload, 'plan_upsert'));
}

export async function archiveMembershipPlan(id, active) {
  return normalizePlan(await post('/MembershipAdmin/plan_archive', { id: String(id), active: Boolean(active) }, 'plan_archive'));
}

export async function duplicateMembershipPlan(id) {
  return normalizePlan(await post('/MembershipAdmin/plan_duplicate', { id: String(id) }, 'plan_duplicate'));
}

// --- Abonnements (souscriptions) -----------------------------------------------------------------------------------

function subscriptionParams({ planId, playerId, status, paymentStatus, paymentMethod, search, view, page = 1, pageSize = 50 } = {}) {
  const params = { page, page_size: pageSize };
  if (playerId) params.player_id = String(playerId);
  if (planId) params.plan_id = planId;
  if (status) params.status = status;
  if (paymentStatus) params.payment_status = paymentStatus;
  if (paymentMethod) params.payment_method = paymentMethod;
  if (search) params.search = search;
  if (view) params.view = view;
  return params;
}

export async function listMembershipSubscriptions(opts = {}) {
  const params = subscriptionParams(opts);
  const { data } = await v2Api.get('/MembershipAdmin/subscriptions', { params });
  return {
    items: (Array.isArray(data?.items) ? data.items : []).map(normalizeSubscription).filter(Boolean),
    total: data?.total ?? 0,
    page: data?.page ?? params.page,
    pageSize: data?.page_size ?? params.page_size,
    summary: data?.summary ?? {
      effective_count: 0,
      pending_payment_count: 0,
      pending_approval_count: 0,
      collected_online_cents: 0,
      collected_on_site_cents: 0,
      refunded_cents: 0,
    },
  };
}

export async function listMembershipEvents(subscriptionId) {
  const { data } = await v2Api.get(`/MembershipAdmin/events/${encodeURIComponent(subscriptionId)}`);
  return Array.isArray(data) ? data : [];
}

/** Télécharge le CSV (filtres courants) — déclenche le téléchargement navigateur. */
export async function exportMembershipSubscriptions(opts = {}) {
  const params = subscriptionParams({ ...opts, page: 1, pageSize: 500 });
  const res = await v2Api.get('/MembershipAdmin/export', { params, responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `adhesions-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function markMembershipPaid({ id, method, reference, note }) {
  return normalizeSubscription(await post('/MembershipAdmin/mark_paid', { id: String(id), method, reference: reference || null, note: note || null }, 'mark_paid'));
}

export async function approveMembership({ id, note }) {
  return normalizeSubscription(await post('/MembershipAdmin/approve', { id: String(id), note: note || null }, 'approve'));
}

export async function rejectMembership({ id, reason, refund = true }) {
  return normalizeSubscription(await post('/MembershipAdmin/reject', { id: String(id), reason, refund: Boolean(refund) }, 'reject'));
}

export async function cancelMembership({ id, reason, refund = false, refundCents = null }) {
  const payload = { id: String(id), reason, refund: Boolean(refund) };
  if (refund && refundCents != null) payload.refund_cents = refundCents;
  return normalizeSubscription(await post('/MembershipAdmin/cancel', payload, 'cancel'));
}

export async function updateMembershipNote({ id, note }) {
  return normalizeSubscription(await post('/MembershipAdmin/note', { id: String(id), note: note || null }, 'note'));
}

export async function adminCreateMembership({ playerId, planId, paid, method, reference, note, approve }) {
  return normalizeSubscription(
    await post(
      '/MembershipAdmin/create',
      {
        player_id: String(playerId),
        plan_id: String(planId),
        paid: Boolean(paid),
        method: method || null,
        reference: reference || null,
        note: note || null,
        approve: Boolean(approve),
      },
      'create'
    )
  );
}

export const membershipsService = {
  listPlans: listMembershipPlans,
  upsertPlan: upsertMembershipPlan,
  archivePlan: archiveMembershipPlan,
  duplicatePlan: duplicateMembershipPlan,
  listSubscriptions: listMembershipSubscriptions,
  listEvents: listMembershipEvents,
  exportCsv: exportMembershipSubscriptions,
  markPaid: markMembershipPaid,
  approve: approveMembership,
  reject: rejectMembership,
  cancel: cancelMembership,
  updateNote: updateMembershipNote,
  adminCreate: adminCreateMembership,
};
